/**
 * Environment variable validation and configuration
 * Ensures all required environment variables are present and valid
 */

import { log } from './logger';

export interface EnvironmentConfig {
  supabase: {
    url: string;
    publishableKey: string;
    isConfigured: boolean;
  };
  app: {
    mode: 'development' | 'production' | 'test';
    useLocalStorage: boolean;
  };
  features: {
    enableLogging: boolean;
    enableAnalytics: boolean;
    enableDebugMode: boolean;
  };
}

class EnvironmentValidator {
  private config: EnvironmentConfig | null = null;
  private validationErrors: string[] = [];

  validate(): EnvironmentConfig {
    if (this.config) {
      return this.config;
    }

    this.validationErrors = [];

    // Validate and build configuration
    this.config = {
      supabase: this.validateSupabaseConfig(),
      app: this.validateAppConfig(),
      features: this.validateFeatureFlags(),
    };

    // Log validation results
    if (this.validationErrors.length > 0) {
      log.warn('Environment validation issues found', 'env-validator', {
        errors: this.validationErrors,
        hasSupabase: this.config.supabase.isConfigured,
      });
    } else {
      log.info('Environment validation successful', 'env-validator', {
        mode: this.config.app.mode,
        hasSupabase: this.config.supabase.isConfigured,
      });
    }

    return this.config;
  }

  private validateSupabaseConfig() {
    const url = import.meta.env.VITE_SUPABASE_URL || '';
    const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '';

    if (!url) {
      this.addError('VITE_SUPABASE_URL is not set');
    } else if (!this.isValidSupabaseUrl(url)) {
      this.addError(
        'VITE_SUPABASE_URL does not appear to be a valid Supabase URL'
      );
    }

    if (!publishableKey) {
      this.addError('VITE_SUPABASE_PUBLISHABLE_KEY is not set');
    } else if (!this.isValidSupabaseKey(publishableKey)) {
      this.addError(
        'VITE_SUPABASE_PUBLISHABLE_KEY does not appear to be a valid Supabase key'
      );
    }

    const isConfigured =
      url &&
      publishableKey &&
      this.isValidSupabaseUrl(url) &&
      this.isValidSupabaseKey(publishableKey);

    return {
      url,
      publishableKey,
      isConfigured: !!isConfigured,
    };
  }

  private validateAppConfig() {
    const mode = this.getAppMode();
    const useLocalStorage = import.meta.env.VITE_USE_LOCAL_STORAGE === 'true';

    return {
      mode,
      useLocalStorage,
    };
  }

  private validateFeatureFlags() {
    const enableLogging = import.meta.env.VITE_ENABLE_LOGGING !== 'false';
    const enableAnalytics = import.meta.env.VITE_ENABLE_ANALYTICS === 'true';
    const enableDebugMode =
      import.meta.env.DEV || import.meta.env.VITE_DEBUG === 'true';

    return {
      enableLogging,
      enableAnalytics,
      enableDebugMode,
    };
  }

  private getAppMode(): 'development' | 'production' | 'test' {
    if (import.meta.env.MODE === 'test') return 'test';
    if (import.meta.env.DEV) return 'development';
    return 'production';
  }

  private isValidSupabaseUrl(url: string): boolean {
    try {
      const parsedUrl = new URL(url);
      return (
        parsedUrl.hostname.includes('supabase.co') ||
        parsedUrl.hostname.includes('supabase.net') ||
        // Allow localhost for development
        parsedUrl.hostname === 'localhost' ||
        parsedUrl.hostname === '127.0.0.1'
      );
    } catch {
      return false;
    }
  }

  private isValidSupabaseKey(key: string): boolean {
    return (
      key.length > 20 &&
      (key.startsWith('sb_publishable_') || // new opaque publishable key
        key.startsWith('sb_secret_') || // new opaque secret key
        key.startsWith('eyJ') || // legacy JWT (anon / service_role)
        /^[a-zA-Z0-9_-]+$/.test(key))
    );
  }

  private addError(error: string): void {
    this.validationErrors.push(error);
  }

  getValidationErrors(): string[] {
    return [...this.validationErrors];
  }

  hasErrors(): boolean {
    return this.validationErrors.length > 0;
  }

  // Utility methods for checking specific configurations
  isSupabaseConfigured(): boolean {
    return this.validate().supabase.isConfigured;
  }

  isDevelopment(): boolean {
    return this.validate().app.mode === 'development';
  }

  isProduction(): boolean {
    return this.validate().app.mode === 'production';
  }

  shouldUseLocalStorage(): boolean {
    return this.validate().app.useLocalStorage;
  }

  isFeatureEnabled(feature: keyof EnvironmentConfig['features']): boolean {
    return this.validate().features[feature];
  }
}

// Export singleton instance
export const envValidator = new EnvironmentValidator();

// Export the validated configuration
export const env = envValidator.validate();

// Export utility functions
export const isSupabaseConfigured = () => envValidator.isSupabaseConfigured();
export const isDevelopment = () => envValidator.isDevelopment();
export const isProduction = () => envValidator.isProduction();
export const shouldUseLocalStorage = () => envValidator.shouldUseLocalStorage();
export const isFeatureEnabled = (
  feature: keyof EnvironmentConfig['features']
) => envValidator.isFeatureEnabled(feature);

// Export for testing
export const getValidationErrors = () => envValidator.getValidationErrors();
export const hasValidationErrors = () => envValidator.hasErrors();
